import React, { PropsWithChildren, useRef } from 'react';
import { DownloadByteUnit } from './partials/DownloadByteUnit';
import { DownloadFileIcon } from './DownloadFileIcon/DownloadFileIcon';
import { Tooltip } from '../../../components/atoms/Tooltip';
import { BADGE_STATUS, StatusBadge } from '../Badge';
import { getFileDescription, getFileExtensionDescription } from './Download.utils';
import { DownloadContainer } from './partials/DownloadContainer';
import { DownloadDescription } from './partials/DownloadDescription';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';
import { getDisplayName, IFileItem } from '../../../types/api/FileItem';
import { DownloadAbbr } from './partials/DownloadAbbr';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import bem from '../../../lib/bemUtils/bem';
import { DownloadFileLinkIcon } from './partials/DownloadFileLinkIcon';

type Download = Omit<IFileItem, 'fileState' | 'id' | 'mimeType'> & {
  archived?: boolean;
  archiveDate?: string;
  snippet?: string;
  mimeType?: string;
  download?: string; // default filename for the Browser when downloaded (if no content-disposition header)
  approximateFileSize?: boolean; // if true, mark the file size as approximate value ("ca." prefix)
};

/**
 * With this component you can download a file.
 * It informs you about the type and the file size.
 */
export function Download({
  url,
  fileExtension,
  mimeType,
  fileSizeBytes,
  archived,
  archiveDate,
  snippet,
  fileLink,
  fileName,
  fileTitle,
  download,
  approximateFileSize = false,
  children,
}: PropsWithChildren<Download>) {
  const uId = useHtmlValidId();
  const aRef = useRef<HTMLAnchorElement>(null);

  const idDescription = `description-${uId}`;

  const displayName = getDisplayName(fileName, fileTitle);

  const { t } = useSonTranslation('glossary');

  return (
    <DownloadContainer snippet={snippet} title={displayName}>
      {/* no <Hyperlink> here, because we do not route downloads! */}
      <Tooltip attachTo={aRef}>
        <div className="download__link-wrapper">
          <a
            className={bem('download__link', fileLink ? 'file-link' : undefined)}
            href={url}
            type={mimeType}
            ref={aRef}
            aria-describedby={idDescription}
            title={getFileDescription(fileExtension)}
            download={download}
          >
            <DownloadFileIcon fileExtension={fileExtension} />
            <span className="download__file-title">{children || displayName}</span>
          </a>
          {fileLink && <DownloadFileLinkIcon helpText={t('fileLink')} />}
        </div>

        {archived && <StatusBadge status={BADGE_STATUS.ARCHIVED} archiveDate={archiveDate} />}
      </Tooltip>
      <DownloadDescription id={idDescription}>
        &#040;
        <DownloadAbbr text={fileExtension} title={getFileExtensionDescription(fileExtension)} />
        , <DownloadByteUnit bytes={fileSizeBytes} estimated={approximateFileSize} />
        &#041;
      </DownloadDescription>
    </DownloadContainer>
  );
}
